import type {
  ConversationMessage,
  ConversationSummary,
  CurrentMembership,
  InternalNote,
  MembershipRole,
  TimelineItem,
} from "./types";

const roleRank = {
  STAFF: 1,
  ADMIN: 2,
  OWNER: 3,
} as const satisfies Record<MembershipRole, number>;

export function canAdministerAssignments(role: MembershipRole | undefined) {
  return role ? roleRank[role] >= roleRank.ADMIN : false;
}

export function isAssignedToCurrentMember(
  conversation: ConversationSummary | null,
  membership: CurrentMembership | null,
) {
  return Boolean(
    conversation?.assignee &&
    membership &&
    conversation.assignee.membershipId === membership.membershipId,
  );
}

export function canSendHumanReply(
  conversation: ConversationSummary | null,
  membership: CurrentMembership | null,
) {
  return Boolean(
    conversation?.ownershipState === "HUMAN_OWNED" &&
    isAssignedToCurrentMember(conversation, membership),
  );
}

export function canPrepareAiReceptionistResponse(
  conversation: ConversationSummary | null,
  membership: CurrentMembership | null,
) {
  return Boolean(
    membership &&
    conversation?.state !== "CLOSED" &&
    conversation?.state !== "ARCHIVED" &&
    conversation?.ownershipState === "AI_OWNED" &&
    conversation.systemResponseEligible,
  );
}

export function canAcceptHandoff(
  conversation: ConversationSummary | null,
  membership: CurrentMembership | null,
) {
  return Boolean(
    conversation?.ownershipState === "HANDOFF_REQUESTED" &&
    membership &&
    (!conversation.assignee ||
      isAssignedToCurrentMember(conversation, membership)),
  );
}

export function canOperateConversation(membership: CurrentMembership | null) {
  return Boolean(membership);
}

export function contactLabel(conversation: ConversationSummary) {
  return (
    conversation.contact?.displayName ??
    conversation.contact?.normalizedPhoneIdentity ??
    conversation.contact?.canonicalWhatsAppIdentity ??
    "Unknown contact"
  );
}

export function messagePreview(conversation: ConversationSummary) {
  const message = conversation.lastMessage;

  if (!message) return "No messages yet";
  if (message.messageType === "UNSUPPORTED") {
    return `Unsupported ${message.unsupportedType ?? "message"}`;
  }

  return message.text?.trim() || `${message.direction.toLowerCase()} message`;
}

export function relativeTime(timestamp: number) {
  const formatter = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });
  const diff = timestamp - Date.now();
  const minutes = Math.round(diff / 60000);
  const hours = Math.round(minutes / 60);
  const days = Math.round(hours / 24);

  if (Math.abs(minutes) < 60) return formatter.format(minutes, "minute");
  if (Math.abs(hours) < 24) return formatter.format(hours, "hour");
  return formatter.format(days, "day");
}

export function formatTime(timestamp: number) {
  return new Intl.DateTimeFormat(undefined, {
    hour: "2-digit",
    minute: "2-digit",
    month: "short",
    day: "numeric",
  }).format(new Date(timestamp));
}

export function buildTimeline(
  messages: ConversationMessage[],
  notes: InternalNote[],
) {
  const timeline: TimelineItem[] = [
    ...messages.map((message) => ({
      kind: "message" as const,
      at: message.processedAt,
      message,
    })),
    ...notes.map((note) => ({
      kind: "note" as const,
      at: note.createdAt,
      note,
    })),
  ];

  return timeline.sort((left, right) => left.at - right.at);
}
